/**
 * Admin Reports Page
 *
 * Dashboard displaying diagnostic analytics, trends, and statistics.
 * Protected route requiring admin authentication.
 */

import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import AdminLayout from '../components/Admin/AdminLayout';

function AdminReports() {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [period, setPeriod] = useState('month');
  const [diagnosticType, setDiagnosticType] = useState('all');
  const [reportData, setReportData] = useState(null);

  // Fetch report data
  useEffect(() => {
    const fetchReports = async () => {
      setLoading(true);
      setError(null);

      try {
        const response = await fetch(
          `/api/admin/reports?period=${period}&diagnosticType=${diagnosticType}`,
          {
            credentials: 'include'
          }
        );

        if (response.status === 401) {
          // Session expired - logout and redirect to login
          await logout();
          navigate('/admin/login');
          return;
        }

        const data = await response.json();

        if (data.success) {
          setReportData(data);
        } else {
          setError(data.error || 'Failed to load reports');
        }
      } catch (err) {
        console.error('Reports fetch error:', err);
        setError('Failed to load reports');
      } finally {
        setLoading(false);
      }
    };

    fetchReports();
  }, [period, diagnosticType, navigate, logout]);

  // Export data as CSV
  const handleExportCSV = () => {
    if (!reportData || !reportData.recentCompletions) return;

    const headers = ['Date', 'Type', 'Leader', 'Email', 'Role', 'Score'];
    const rows = reportData.recentCompletions.map(completion => [
      new Date(completion.completedAt).toLocaleDateString(),
      completion.diagnosticType,
      `${completion.firstName} ${completion.lastName}`,
      completion.email,
      completion.leadershipRole || 'N/A',
      completion.systemLoss || completion.burdenScore || 'N/A'
    ]);

    const csv = [
      headers.join(','),
      ...rows.map(row => row.map(cell => `"${cell}"`).join(','))
    ].join('\n');

    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `daring-futures-reports-${period}-${Date.now()}.csv`;
    link.click();
    URL.revokeObjectURL(url);
  };

  // Format period label
  const getPeriodLabel = (p) => {
    const labels = {
      week: 'Last 7 Days',
      month: 'Last 30 Days',
      quarter: 'Last 90 Days',
      year: 'Last Year',
      all: 'All Time'
    };
    return labels[p] || p;
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        {/* Header */}
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-bold text-daring-navy">Reports & Analytics</h1>
            <p className="text-daring-grey mt-2">
              Diagnostic completion trends and insights
            </p>
          </div>
          <button
            onClick={handleExportCSV}
            disabled={!reportData}
            className="bg-daring-teal hover:bg-daring-navy text-white px-4 py-2 rounded disabled:opacity-50 disabled:cursor-not-allowed transition-all"
          >
            Export CSV
          </button>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            {/* Period Filter */}
            <div>
              <label className="block text-sm font-bold text-daring-navy mb-2">
                Time Period
              </label>
              <select
                value={period}
                onChange={(e) => setPeriod(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-daring-teal"
              >
                <option value="week">Last 7 Days</option>
                <option value="month">Last 30 Days</option>
                <option value="quarter">Last 90 Days</option>
                <option value="year">Last Year</option>
                <option value="all">All Time</option>
              </select>
            </div>

            {/* Diagnostic Type Filter */}
            <div>
              <label className="block text-sm font-bold text-daring-navy mb-2">
                Diagnostic Type
              </label>
              <select
                value={diagnosticType}
                onChange={(e) => setDiagnosticType(e.target.value)}
                className="w-full border border-gray-300 rounded px-3 py-2 focus:outline-none focus:ring-2 focus:ring-daring-teal"
              >
                <option value="all">All Diagnostics</option>
                <option value="system_burden">System Burden</option>
                <option value="leadership_burden">Leadership Burden</option>
                <option value="psychological_flexibility">Psychological Flexibility</option>
              </select>
            </div>
          </div>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="bg-white rounded-lg shadow-md p-12 text-center">
            <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-daring-navy mx-auto mb-4"></div>
            <p className="text-daring-grey">Loading reports...</p>
          </div>
        )}

        {/* Error State */}
        {error && (
          <div className="bg-red-50 border-l-4 border-red-500 p-4 rounded">
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Report Data */}
        {!loading && !error && reportData && (
          <>
            {/* Summary Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-daring-grey mb-2">Total Diagnostics</div>
                <div className="text-3xl font-bold text-daring-navy">
                  {reportData.summary.totalDiagnostics}
                </div>
                <div className="text-xs text-daring-grey mt-1">
                  {getPeriodLabel(period)}
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-daring-grey mb-2">Completed</div>
                <div className="text-3xl font-bold text-daring-teal">
                  {reportData.summary.totalCompleted}
                </div>
                <div className="text-xs text-daring-grey mt-1">
                  {reportData.summary.completionRate}% completion rate
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-daring-grey mb-2">Total Leaders</div>
                <div className="text-3xl font-bold text-daring-teal">
                  {reportData.summary.totalLeaders}
                </div>
                <div className="text-xs text-daring-grey mt-1">
                  Registered users
                </div>
              </div>

              <div className="bg-white rounded-lg shadow-md p-6">
                <div className="text-sm text-daring-grey mb-2">
                  {diagnosticType === 'leadership_burden' ? 'Avg Burden' : 'Avg System Loss'}
                </div>
                <div className="text-3xl font-bold text-orange-600">
                  {diagnosticType === 'leadership_burden'
                    ? reportData.summary.avgLeadershipBurden !== null
                      ? `${reportData.summary.avgLeadershipBurden}/10`
                      : 'N/A'
                    : reportData.summary.avgSystemBurden !== null
                    ? `${reportData.summary.avgSystemBurden}%`
                    : 'N/A'
                  }
                </div>
                <div className="text-xs text-daring-grey mt-1">
                  Across completed diagnostics
                </div>
              </div>
            </div>

            {/* By Type Breakdown */}
            {diagnosticType === 'all' && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-daring-navy mb-4">By Diagnostic Type</h2>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div className="border-l-4 border-blue-500 pl-4">
                    <div className="text-lg font-bold text-daring-navy">System Burden</div>
                    <div className="text-sm text-daring-grey mt-2">
                      Total: {reportData.byType.system_burden.total} |
                      Completed: {reportData.byType.system_burden.completed}
                    </div>
                    {reportData.summary.avgSystemBurden !== null && (
                      <div className="text-sm text-daring-grey">
                        Avg Loss: {reportData.summary.avgSystemBurden}%
                      </div>
                    )}
                  </div>

                  <div className="border-l-4 border-purple-500 pl-4">
                    <div className="text-lg font-bold text-daring-navy">Leadership Burden</div>
                    <div className="text-sm text-daring-grey mt-2">
                      Total: {reportData.byType.leadership_burden.total} |
                      Completed: {reportData.byType.leadership_burden.completed}
                    </div>
                    {reportData.summary.avgLeadershipBurden !== null && (
                      <div className="text-sm text-daring-grey">
                        Avg Burden: {reportData.summary.avgLeadershipBurden}/10
                      </div>
                    )}
                  </div>
                </div>
              </div>
            )}

            {/* Top Anxieties (Leadership Burden only) */}
            {reportData.topAnxieties && reportData.topAnxieties.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-daring-navy mb-4">
                  Top 5 Anxieties (Leadership Burden)
                </h2>
                <div className="space-y-3">
                  {reportData.topAnxieties.map((anxiety, index) => (
                    <div key={anxiety.id} className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <div className="text-2xl font-bold text-daring-grey">
                          {index + 1}
                        </div>
                        <div>
                          <div className="font-bold text-daring-navy">{anxiety.name}</div>
                          <div className="text-sm text-daring-grey">
                            Average Score: {anxiety.averageScore}/10
                          </div>
                        </div>
                      </div>
                      <div className="w-32 bg-gray-200 rounded-full h-2">
                        <div
                          className="bg-daring-teal h-2 rounded-full"
                          style={{ width: `${(anxiety.averageScore / 10) * 100}%` }}
                        ></div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Recent Completions */}
            <div className="bg-white rounded-lg shadow-md p-6">
              <h2 className="text-xl font-bold text-daring-navy mb-4">Recent Completions</h2>

              {reportData.recentCompletions.length === 0 ? (
                <p className="text-daring-grey text-center py-8">
                  No completions found for the selected period.
                </p>
              ) : (
                <div className="overflow-x-auto">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-3 text-left text-xs font-bold text-daring-navy uppercase">
                          Date
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-daring-navy uppercase">
                          Type
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-daring-navy uppercase">
                          Leader
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-daring-navy uppercase">
                          Role
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-daring-navy uppercase">
                          Score
                        </th>
                        <th className="px-4 py-3 text-left text-xs font-bold text-daring-navy uppercase">
                          Actions
                        </th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {reportData.recentCompletions.map((completion, index) => (
                        <tr key={`${completion.diagnosticType}-${completion.id}`} className="hover:bg-gray-50">
                          <td className="px-4 py-3 whitespace-nowrap text-sm text-daring-grey">
                            {new Date(completion.completedAt).toLocaleDateString()}
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                              completion.diagnosticType === 'System Burden'
                                ? 'bg-blue-100 text-blue-800'
                                : 'bg-purple-100 text-purple-800'
                            }`}>
                              {completion.diagnosticType}
                            </span>
                          </td>
                          <td className="px-4 py-3 text-sm text-daring-navy">
                            {completion.firstName} {completion.lastName}
                          </td>
                          <td className="px-4 py-3 text-sm text-daring-grey">
                            {completion.leadershipRole || 'N/A'}
                          </td>
                          <td className="px-4 py-3 text-sm font-semibold text-daring-navy">
                            {completion.diagnosticType === 'System Burden'
                              ? `${completion.systemLoss}%`
                              : `${completion.burdenScore}/10`
                            }
                          </td>
                          <td className="px-4 py-3 text-sm">
                            {completion.hubspotContactLink && (
                              <a
                                href={completion.hubspotContactLink}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-daring-teal hover:text-daring-navy"
                              >
                                View in HubSpot →
                              </a>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>

            {/* Simple Trends Visualization */}
            {reportData.trends && reportData.trends.length > 0 && (
              <div className="bg-white rounded-lg shadow-md p-6">
                <h2 className="text-xl font-bold text-daring-navy mb-4">Completion Trends</h2>
                <div className="text-sm text-daring-grey mb-4">
                  Daily completions over {getPeriodLabel(period).toLowerCase()}
                </div>
                <div className="space-y-2">
                  {/* Group trends by date and sum counts */}
                  {Object.entries(
                    reportData.trends.reduce((acc, trend) => {
                      if (!acc[trend.date]) acc[trend.date] = 0;
                      acc[trend.date] += trend.count;
                      return acc;
                    }, {})
                  ).map(([date, count]) => (
                    <div key={date} className="flex items-center space-x-3">
                      <div className="text-sm text-daring-grey w-32">
                        {new Date(date).toLocaleDateString()}
                      </div>
                      <div className="flex-1 bg-gray-200 rounded-full h-4">
                        <div
                          className="bg-daring-teal h-4 rounded-full flex items-center justify-end pr-2"
                          style={{ width: `${Math.min((count / 5) * 100, 100)}%` }}
                        >
                          <span className="text-xs text-white font-semibold">{count}</span>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </AdminLayout>
  );
}

export default AdminReports;
